/* ============================================================
   Solskydd PDP  –  section-main-product-solskydd.js

   Behaviour for the blocks the solskydd template adds on top of the
   theme's own product page. Everything the theme already owns - the
   media slider, thumbnails, zoom, variant picker, quantity input and
   the made-to-measure customizer - is left alone; this file only wires
   the new blocks and listens to the theme's events.

   The template is a visual upgrade: how a product is bought is the old
   page's. The customizer (another company's code) is never called,
   patched or intercepted from here - no limits, labels, prices, buttons
   or instance state are written, and none of its events is stopped. Its
   fields are styled by the stylesheet. Two controls differ from the old
   page, and both pick the theme's own radios: a long fixed-size row
   shown as a dropdown, and a height span chosen from the typed height
   instead of a row of pills. A span switch rebuilds the customizer, and
   around it heightSpans is the only place this file touches its fields,
   each time as a shopper could: width and height are emptied just before
   the switch, its text and number fields are inert during it, and the
   shopper's entries are typed back afterwards. Its fields grid also holds
   one line of ours (measureNotes) that says why a measurement cannot be
   ordered.

   All hooks are data-attributes so markup and behaviour stay separate.
   ============================================================ */

(function () {
  'use strict';

  var money = function (cents) {
    if (window.Woolman && Woolman.Utils && Woolman.Utils.formatMoney) {
      return Woolman.Utils.formatMoney(cents);
    }
    return (cents / 100).toFixed(0) + ' kr';
  };

  /* ---------- equal-width variant pills -----------------------
     The stylesheet sizes the pills from --rd-seg-w with a hardcoded
     fallback. Without this the fallback wins, so the pills are a fixed
     width regardless of their labels - fine for "132 cm", too narrow for a
     longer value. Measure the widest label's natural width and lock every
     pill to it, so the rows stay equal length and nothing clips.
     Re-measured on resize, once webfonts land, and after a variant change,
     because the theme re-renders the option labels by id.          */
  function segmentWidth(root) {
    var panel = root.querySelector('.rd-options');
    if (!panel) return;

    function measure() {
      // colour chips and icon tiles size themselves (see the solskydd block at
      // the end of the stylesheet) - measured, they stretched every pill.
      // Rows turned into a dropdown are left out too: their labels are hidden
      // (see optionSelects below)
      var pills = panel.querySelectorAll('.product-form__input:not(.rd-as-select) label:not(.swatch):not(.field__label--image)');
      // a zero-width panel (hidden tab, preview) would measure to nothing and
      // lock the pills to a wrong width - skip and retry on the next resize
      if (!pills.length || !panel.getBoundingClientRect().width) return;

      var previous = panel.style.getPropertyValue('--rd-seg-w');
      panel.style.removeProperty('--rd-seg-w');
      panel.classList.add('rd-mater');            // width: max-content
      var widest = 0;
      Array.prototype.forEach.call(pills, function (l) {
        widest = Math.max(widest, l.getBoundingClientRect().width);
      });
      panel.classList.remove('rd-mater');
      // a real pill is wider than 20px; anything less is a hidden label, and
      // locking to it would collapse every pill on the page
      if (widest > 20) panel.style.setProperty('--rd-seg-w', Math.ceil(widest) + 'px');
      else if (previous) panel.style.setProperty('--rd-seg-w', previous);
    }

    measure();
    window.addEventListener('resize', debounce(measure, 150));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    // optionSelects() asks for a fresh measure before and after every swap
    root.addEventListener('solskydd:measure', measure);
    // the theme swaps variant-selects on change, replacing the label elements
    root.addEventListener('change', function (e) {
      if (e.target.closest('.product-form__input')) setTimeout(measure, 0);
    });
    document.addEventListener('variant:changed', function () { setTimeout(measure, 0); });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  /* ---------- long fixed-size rows -> dropdown ------------------
     A segmented control reads well on one row. Bredd on a stock product
     runs to eleven fixed widths, and eleven pills wrap into a block that
     scans like a table. Where the pills of a row do not fit one line they
     are hidden, and a native <select> drives the same radios, so the
     theme's variant logic sees an ordinary pick.

     Fixed sizes only: the picker wrapper carries rd-fixed-sizes when no
     variant is made to measure (solskydd-product-template.liquid). On a
     made-to-measure product the rows are spans the shopper then types an
     exact measure into; they never convert and stay pills, as on the old
     page. Colour chips never convert.

     Traps, all paid for in the first build of this (lost 2026-09-25):
     - Fit is computed - pill count times --rd-seg-w, less the 1px the
       neighbours overlap - never read off label positions, which all look
       equal before layout settles, so a converted row silently reverted.
     - segmentWidth() leaves converted rows out of its measure; their
       hidden labels would lock every pill on the page to nothing.
     - Unavailable values carry class="disabled", not the property. A
       dropdown has no diagonal, so the option text says it, in the
       theme's own words (products.product.value_unavailable).
     - Never rebuild while the dropdown has focus: replacing the element
       closes an open native list under the shopper's pointer.
     And one of the theme's: the select's own change event must not reach
     <variant-selects>, whose updateSelectionMetadata() looks for an option
     with a selected ATTRIBUTE and throws without one. The select stops it
     and fires change on the radio instead.

     The theme replaces the picker's children on every variant change, so
     a MutationObserver on <variant-selects> re-applies. It watches direct
     children only; the select goes inside a fieldset, so adding it cannot
     re-trigger the observer.                                        */
  function optionSelects(root) {
    var picker = root.querySelector('.rd-options .rd-fixed-sizes variant-selects');
    if (!picker) return;
    var unavailable = root.dataset.valueUnavailable || '__VALUE__';
    var waiting = false;
    // the option name whose dropdown had focus when it changed the variant,
    // so the rebuilt one gets the focus back and arrow keys keep working
    var refocus = '';

    function segmentW(fieldset) {
      var w = parseFloat(getComputedStyle(fieldset).getPropertyValue('--rd-seg-w'));
      return w > 20 ? w : 112;   // the stylesheet's own fallback
    }

    function optionName(fieldset) {
      var legend = fieldset.querySelector(':scope > legend');
      return (legend && legend.getAttribute('data-legend')) || '';
    }

    function inUse() {
      var el = document.activeElement;
      return !!(el && el.closest && el.closest('[data-solskydd-select]'));
    }

    function revert(fieldset) {
      var wrap = fieldset.querySelector(':scope > [data-solskydd-select]');
      if (wrap) wrap.remove();
      fieldset.classList.remove('rd-as-select');
      delete fieldset.dataset.rdSegW;
      fieldset.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.removeAttribute('tabindex');
        radio.removeAttribute('aria-hidden');
      });
    }

    function convert(fieldset, radios) {
      // the pill type, read while the pills still show
      var pill = fieldset.querySelector('label');
      var type = pill ? getComputedStyle(pill) : null;
      var name = optionName(fieldset);

      var wrap = document.createElement('div');
      wrap.className = 'rd-optsel';
      wrap.setAttribute('data-solskydd-select', '');
      var select = document.createElement('select');
      select.className = 'rd-optsel__select';
      select.setAttribute('aria-label', name || radios[0].name);
      if (type) {
        select.style.fontFamily = type.fontFamily;
        select.style.fontSize = type.fontSize;
        select.style.letterSpacing = type.letterSpacing;
      }
      radios.forEach(function (radio) {
        var option = document.createElement('option');
        option.value = radio.id;
        option.textContent = radio.classList.contains('disabled')
          ? unavailable.replace('__VALUE__', radio.value)
          : radio.value;
        // the property, never the attribute - getSelectedOptionValues()
        // would read an option[selected] as one of the picked values
        if (radio.checked) option.selected = true;
        select.appendChild(option);
      });
      select.addEventListener('change', function (e) {
        e.stopPropagation();
        var radio = document.getElementById(select.value);
        if (!radio || radio.checked) return;
        refocus = name;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrap.appendChild(select);
      fieldset.appendChild(wrap);
      fieldset.classList.add('rd-as-select');
      // one control, not two: the radios stay for the theme, out of reach
      radios.forEach(function (radio) {
        radio.setAttribute('tabindex', '-1');
        radio.setAttribute('aria-hidden', 'true');
      });
      if (refocus && refocus === name) {
        refocus = '';
        select.focus({ preventScroll: true });
      }
    }

    function apply() {
      if (inUse()) {
        if (!waiting) {
          waiting = true;
          document.activeElement.addEventListener('blur', function () {
            waiting = false;
            apply();
          }, { once: true });
        }
        return;
      }
      // measure first: a row the theme has just re-rendered shows its pills
      // again, and --rd-seg-w must count them before the fit test does
      root.dispatchEvent(new CustomEvent('solskydd:measure'));
      var changed = false;
      picker.querySelectorAll(':scope > fieldset.product-form__input').forEach(function (fieldset) {
        if (fieldset.querySelector('label.swatch')) return;
        if (getComputedStyle(fieldset).display === 'none') return;
        var radios = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
        var width = fieldset.clientWidth;
        if (!radios.length || !width) return;
        var converted = fieldset.classList.contains('rd-as-select');
        // a converted row's pills are hidden and no longer measured, so it is
        // tested with the width they had when it converted - otherwise the
        // narrower pills left elsewhere would let it flip back and forth
        var pill = segmentW(fieldset);
        if (converted) pill = Math.max(pill, parseFloat(fieldset.dataset.rdSegW) || 0);
        var fits = radios.length * pill - (radios.length - 1) <= width;
        if (fits && converted) {
          revert(fieldset);
          changed = true;
        } else if (!fits && !converted) {
          fieldset.dataset.rdSegW = pill;
          convert(fieldset, radios);
          changed = true;
        }
      });
      // the pills still showing may size differently without (or with) a row
      if (changed) root.dispatchEvent(new CustomEvent('solskydd:measure'));
    }

    apply();
    var later = debounce(apply, 200);
    window.addEventListener('resize', later);
    // the row's own width, which can change without a window resize (a
    // device emulator did exactly that). Width only: a swap changes the
    // picker's height, and reacting to that would call apply for nothing
    if ('ResizeObserver' in window) {
      var lastWidth = picker.clientWidth;
      new ResizeObserver(function () {
        var width = picker.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        later();
      }).observe(picker);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
    if ('MutationObserver' in window) {
      new MutationObserver(apply).observe(picker, { childList: true });
    }
  }

  /* ---------- what cannot be ordered, in words -----------------
     The customizer rejects a measurement with a red edge and a locked buy
     button and says nothing more. This page adds states the old one never
     had - heightSpans below types a width back into a span that may allow
     less of it - so one line of ours under the width and height says why
     what is typed cannot be ordered, in the section's own words (its
     "Måttfält" settings, printed by solskydd-product-template.liquid as
     data-solskydd-messages). On every made-to-measure product:
     - the height: on a product sold in height spans heightSpans says it
       (outside the colour's range, between two spans, a span the colour
       lacks, a failed update); elsewhere the field's own min and max
     - the width, against the field's own min and max. Where a colour's
       spans differ in width (Mellanglaspersienn: 200 cm up to 120 cm high,
       100 cm at 201-220) the text names the height it depends on
     - valid measurements the customizer finds no price for (a data error):
       its button stays locked, and this is the only word about it
     An empty field is not a problem, only unfinished: its hint says what
     goes in it.
     The rules are the customizer's, read and copied here, never called
     (validateInputs in component-customizer.js): a number above 0 within
     the input's min and max. Its fields, prices and events are left alone.
     The line is a <p> of ours in its fields grid, with its own class and
     no id, which its observer (it looks for ids with "customizer") passes
     by. It is always there as a live region, empty when all is well - a
     region added or shown together with its text often goes unannounced -
     and it is put back, empty, each time the theme rebuilds the fields. */
  function measureNotes(root) {
    var id = root.id;
    var texts = {};
    var source = root.querySelector('[data-solskydd-messages]');
    try { texts = source ? JSON.parse(source.textContent) || {} : {}; } catch (e) { texts = {}; }
    var FIELDS = ['max-width-', 'max-height-', 'mounting-height-'];
    // the theme locks the buy button 100 ms after a rebuild and the new
    // customizer judges it 100 ms after that (global.js
    // checkCustomizerFields, component-customizer.js setupEventListeners):
    // until then the button says nothing about the price
    var AFTER_REBUILD = 250;
    var parts = { height: '', width: '', price: '' };
    var span = null;          // heightSpans' hooks, on a product sold in height spans
    var priceTimer = null;
    var rebuiltAt = 0;

    function input(prefix) { return document.getElementById(prefix + id); }

    // numbers the Swedish way: 80,5, and no ",0"
    function num(n) {
      return typeof n === 'number' && !isNaN(n) ? String(Math.round(n * 100) / 100).replace('.', ',') : '';
    }

    // a text from the settings with its [placeholders] filled in
    function format(kind, values) {
      var text = String(texts[kind] || '');
      Object.keys(values || {}).forEach(function (k) {
        var v = values[k];
        var shown = typeof v === 'number' ? num(v) : (v === null || v === undefined ? '' : String(v));
        text = text.split('[' + k + ']').join(shown);
      });
      return text;
    }

    // the customizer's own test (validateInputs, read): a number above 0
    // within the input's min and max. null for no field or an empty one
    function check(field) {
      if (!field) return null;
      var text = String(field.value || '').trim();
      if (!text) return null;
      var value = parseFloat(text);
      var min = parseFloat(field.getAttribute('min')) || 0;
      var max = parseFloat(field.getAttribute('max')) || 999999;
      return { ok: !isNaN(value) && value >= min && value <= max && value > 0, value: value, min: min, max: max, text: text };
    }

    // a whole number that more digits could still make valid - "3" on the
    // way to 35. A field without a max reads 999999, so there every whole
    // number may grow and the line waits until the field is left; the red
    // edge still comes at once, from the customizer
    function mayGrow(c) {
      return /^\d+$/.test(c.text) && Number(c.text) * 10 <= c.max;
    }

    function typing(e, prefix) {
      return !!e && e.type === 'input' && e.target === input(prefix);
    }

    function widthPart(e) {
      var w = check(input('max-width-'));
      if (!w || w.ok || (typing(e, 'max-width-') && mayGrow(w))) return '';
      var h = check(input('max-height-'));
      if (span && h && h.ok && span.widthVaries()) {
        return format('width_height', { height: h.value, min: w.min, max: w.max });
      }
      return format('width', { min: w.min, max: w.max });
    }

    // on a product sold in height spans, heightSpans says it instead
    function heightPart(e) {
      if (span) return parts.height;
      var h = check(input('max-height-'));
      if (!h || h.ok || (typing(e, 'max-height-') && mayGrow(h))) return '';
      return format('range', { min: h.min, max: h.max });
    }

    // Valid measurements and still no price: every field filled, width and
    // height within their ranges, the buy button locked, and the hidden
    // total empty or 0 (hasValidPriceInformation, read). Each of these alone
    // also happens for other reasons - the lock after every rebuild, an
    // empty total where the customizer prices nothing - so all of them.
    // Read after the customizer's own handlers have run.
    function pricePart() {
      if (span && span.busy()) return '';
      if (Date.now() - rebuiltAt < AFTER_REBUILD) return '';
      var present = FIELDS.map(function (prefix) { return input(prefix); }).filter(Boolean);
      if (!present.length) return '';
      for (var i = 0; i < present.length; i++) {
        if (!String(present[i].value || '').trim()) return '';
        // any mounting height is valid to the customizer, text included
        if (present[i] !== input('mounting-height-') && !check(present[i]).ok) return '';
      }
      var form = input('product-form-');
      var button = form && form.querySelector('[name="add"]');
      var total = input('custom-total-price-');
      if (!button || !button.disabled || !total) return '';
      return parseFloat(String(total.value).replace(',', '.')) > 0 ? '' : format('price', {});
    }

    // under the height, or under the width where there is no height field
    function line() {
      var anchor = input('max-height-') || input('max-width-');
      var cell = anchor && anchor.closest('.field');
      if (!cell || !cell.parentNode) return null;
      var el = cell.parentNode.querySelector(':scope > .rd-span-msg');
      if (!el) {
        el = document.createElement('p');
        el.className = 'rd-span-msg';
        el.setAttribute('role', 'status');
        cell.parentNode.insertBefore(el, cell.nextSibling);
      }
      return el;
    }

    function paint() {
      var el = line();
      if (!el) return;
      var text = [parts.height, parts.width, parts.price].filter(Boolean).join(' ');
      if (el.textContent !== text) el.textContent = text;
    }

    function judge(e) {
      parts.width = widthPart(e);
      parts.height = heightPart(e);
      paint();
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () {
        parts.price = pricePart();
        paint();
      }, 0);
    }

    function reset() {
      clearTimeout(priceTimer);
      parts = { height: '', width: '', price: '' };
      paint();
    }

    function onEvent(e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT' || !FIELDS.some(function (prefix) { return t.id === prefix + id; })) return;
      // a span switch empties the fields and types them back: judged then
      if (span && span.busy()) return;
      judge(e);
    }

    return {
      format: format,
      // heightSpans: its hooks (busy, widthVaries), and its word on the height
      attach: function (hooks) { span = hooks; },
      height: function (text) { parts.height = text || ''; paint(); },
      refresh: function () { judge(null); },
      reset: reset,
      // called after heightSpans has bound its listeners (see init)
      listen: function () {
        root.addEventListener('input', onEvent);
        root.addEventListener('change', onEvent);
        // the theme rewrites #customizer- on every variant change; a new
        // height (or width) field means rebuilt fields, and the old line
        // went with the old ones
        var known = input('max-height-') || input('max-width-');
        var box = input('customizer-');
        if (box && 'MutationObserver' in window) {
          new MutationObserver(function () {
            var now = input('max-height-') || input('max-width-');
            if (now === known) return;
            known = now;
            rebuiltAt = Date.now();
            reset();
          }).observe(box, { childList: true, subtree: true });
        }
        paint();
      }
    };
  }

  /* ---------- height span from the typed height -----------------
     Some products sell their height in spans - "Höjd (intervall)":
     20-60 cm, 61-80 cm, ... - and every span variant carries its own
     limits and price table (custom.custom_products). The page shows one
     height field instead of the span row: solskydd-spans.liquid hides the
     row and prints each span's real limits (labels drift from the data).

     Typing a height does what a click on its span did on the old page,
     and nothing more: a height that belongs to another span selects that
     span through the theme's own picker - the hidden radio plus a change
     event - and the theme refetches the section and rebuilds the
     customizer with that span's limits and table. The rebuild empties the
     fields, so what the shopper entered is typed back in afterwards.

     When: at once when the number is complete (150 on a blind that stops
     at 300 cannot grow into another height). A number that can still grow
     into a longer valid height - 22 on a plissé that goes to 240 may be
     the start of 220 - waits for the next key, for the field to be left,
     or for Enter; meanwhile the customizer shows its own red, as the old
     page did for a height outside the chosen span. No timer: a switch
     takes the fields for about half a second, and a key typed meanwhile
     would be lost ("22", a pause, "0" once ordered a 22 cm blind).

     The customizer is used here only the way a shopper uses it: its
     events run as they always do, it validates and prices every value
     itself, and its prices, limits, labels, buttons, hidden fields and
     instance are never written. The contacts with its fields, all as a
     shopper's own actions would be:
     - Just before the switch the width and height are emptied (value plus
       input and change). Every customizer instance ever made keeps
       listening, and 150 ms after any variant change each reads its base
       price back off the price on display (component-customizer.js
       setupVariantChangeListener -> refreshData -> getBasePrice, read,
       not changed). With entries in the fields that price holds them and
       every instance adds them again - 5 727 kr for 1 909 after one
       switch, and after a refetch that failed the next valid height sent
       33 904 kr instead of 2 119 kr to the cart. Emptied fields show the
       plain base. Only width and height feed the price.
     - The field the shopper is in is left before the swap (theme-gotchas
       38: removing a focused, edited field fires its change mid-swap, and
       the new instance inherits a doubled base - 11 264 kr for 1 719 kr,
       seen), and the text and number fields are inert until the entries
       are back: typed into before the new instance exists (100 ms after
       the swap), they would feed it the wrong base too. Vägg/Tak and the
       other radios stay live, so a click on them during the switch counts.
     - SETTLE ms after the rebuild - once the new instance exists and has
       priced its empty fields - the entries are typed back in: the value
       plus the input and change events that typing and leaving the field
       give. The radios, which do not feed the price, go back at once,
       before the next paint, as a click on them would. Only after a switch
       made here; a colour the shopper picks gets the theme's usual fresh
       fields.

     What cannot be ordered is said in words, on measureNotes' line under
     the width and height (problem(), say()): a height outside the colour's
     range, one between two spans, one whose span this colour lacks or
     cannot sell - the theme's own picker tells, as it renders no value that
     does not exist with the colour and marks one it cannot sell
     class="disabled" - and an update that failed. Such a height is not
     switched to: the page stays on its span, and the customizer's red and
     the line say why. With the span buttons gone, this is how the old
     page's crossed-out pills speak now. A width the new span does not
     allow, once typed back, is measureNotes' own to say. The line is the
     fourth contact: an element of ours inside the customizer's fields
     grid, with its own class and no id.

     A refetch that fails leaves the page as the theme leaves it after a
     failed span click on the old page ("Ej tillgänglig"); the emptied
     width and height keep the price right, and the shopper types them
     again. The sign is the theme's own "unavailable" text on the buy
     button while a switch is out, or FAIL_MS without a rebuild. */
  function heightSpans(root, notes) {
    var dataEl = root.querySelector('[data-solskydd-spans]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var index = data.rangeIndex;
    if (typeof index !== 'number' || index < 0) return;

    var id = root.id;
    var variants = (data.variants || []).map(function (v) {
      return {
        id: v.id, options: v.options,
        min: parseFloat(v.min), max: parseFloat(v.max), minText: v.min, maxText: v.max,
        minW: parseFloat(v.minW), maxW: parseFloat(v.maxW)
      };
    }).filter(function (v) { return !isNaN(v.min) && !isNaN(v.max); });
    if (variants.length < 2) return;

    var SETTLE = 350;     // ms after the last rebuild before entries go back
    var GRACE = 1500;     // a later rebuild this soon still belongs to the switch
    var FAIL_MS = 8000;   // no rebuild by then: the refetch is taken as failed
    var saved = {};       // field id, or 'radio:' + name -> the shopper's entry
    var pending = null;   // the switch, queued behind the event that asked for it
    var switching = null; // the span a refetch is on its way to
    var watchdog = null;  // ends the wait for a refetch that never lands
    var settle = null;
    var graceUntil = 0;
    var retries = 0;      // switches made since the shopper last typed
    var focusId = null;   // the customizer field the shopper is in
    var clearing = false; // the width and height are being emptied for a switch
    var failWatch = null;
    var knownInput = heightInput();

    function node(prefix) { return document.getElementById(prefix + id); }
    function box() { return node('customizer-'); }
    function heightInput() { return document.getElementById('max-height-' + id); }

    function heightValue() {
      var input = heightInput();
      return input ? parseFloat(String(input.value).replace(',', '.')) : NaN;
    }

    function currentVariant() {
      var form = node('product-form-');
      var input = form && form.querySelector('input[name="id"]');
      var vid = input ? Number(input.value) : NaN;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === vid) return variants[i];
      }
      return null;
    }

    // the spans that share every other option (colour) with the variant
    function spans(cur) {
      return variants.filter(function (v) {
        return v.options.every(function (value, i) { return i === index || value === cur.options[i]; });
      }).sort(function (a, b) { return a.max - b.max || a.min - b.min; });
    }

    // the span of this colour that holds the height, if any. One in a gap
    // between two spans (80.5 between 80 and 81), below or above them all,
    // or in a span this colour lacks has none: problem() says which
    function spanFor(height, cur) {
      if (!(height > 0)) return null;
      var list = spans(cur);
      for (var i = 0; i < list.length; i++) {
        if (list[i].min <= height && height <= list[i].max) return list[i];
      }
      return null;
    }

    // whether the theme's own picker lets the span be bought with this
    // colour: a value that does not exist with it is not rendered at all, one
    // that is not available carries class="disabled"
    // (product-variant-options.liquid, read)
    function spanOff(value) {
      var radio = spanRadio(value);
      return !radio || radio.disabled || radio.classList.contains('disabled') || !radio.getAttribute('data-variant-id');
    }

    // the span the typed height needs, when it is not the one selected and
    // can be bought
    function dueSpan() {
      var cur = currentVariant();
      var target = cur && spanFor(heightValue(), cur);
      if (!target || target.id === cur.id || spanOff(target.options[index])) return null;
      return target;
    }

    // why the typed height cannot be ordered with this colour - for the line
    // under the fields; null when it can, or when the field is empty
    function problem() {
      var cur = currentVariant();
      var h = heightValue();
      if (!cur || !(h > 0)) return null;
      var list = spans(cur);
      if (!list.length) return null;
      var lo = Math.min.apply(null, list.map(function (v) { return v.min; }));
      var hi = Math.max.apply(null, list.map(function (v) { return v.max; }));
      if (h < lo || h > hi) return { kind: 'range', min: lo, max: hi };
      var span = spanFor(h, cur);
      if (span) {
        return span.id !== cur.id && spanOff(span.options[index]) ? { kind: 'unavailable', span: span.options[index] } : null;
      }
      // none of this colour's spans holds it: one another colour has, or a gap
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].min <= h && h <= variants[i].max) return { kind: 'unavailable', span: variants[i].options[index] };
      }
      var below = null;
      var above = null;
      list.forEach(function (v) {
        if (v.max < h && (below === null || v.max > below)) below = v.max;
        if (v.min > h && (above === null || v.min < above)) above = v.min;
      });
      return { kind: 'gap', height: h, below: below, above: above };
    }

    // the message in the section's own words ([min], [span] ...); the
    // numbers are written the Swedish way by measureNotes (80,5, no ",0")
    function words(p) {
      var cur = currentVariant();
      return notes.format(p.kind, {
        min: p.min, max: p.max, height: p.height, below: p.below, above: p.above,
        span: p.span || '',
        colour: cur ? cur.options.filter(function (o, i) { return i !== index; }).join(' / ') : ''
      });
    }

    // the height's part of measureNotes' line under the width and height
    function say(p) {
      notes.height(p ? words(p) : '');
    }

    // whether this colour's spans differ in width (Mellanglaspersienn): then
    // measureNotes' width message names the height it depends on. A span
    // whose data has no width limits is left out of the count
    function widthVaries() {
      var cur = currentVariant();
      if (!cur) return false;
      var seen = {};
      spans(cur).forEach(function (v) {
        if (!isNaN(v.minW) && !isNaN(v.maxW)) seen[v.minW + '-' + v.maxW] = true;
      });
      return Object.keys(seen).length > 1;
    }

    // the number may still become a longer valid height: 22 on a plissé
    // that goes to 240 may be the start of 220. Whole numbers only - one
    // with decimals is as long as it gets
    function canGrow() {
      var input = heightInput();
      var cur = currentVariant();
      var text = input ? String(input.value) : '';
      if (!cur || !/^\d+$/.test(text)) return false;
      var list = spans(cur);
      var top = Math.max.apply(null, list.map(function (v) { return v.max; }));
      return Number(text) * 10 <= top;
    }

    // the whole height on offer with this colour, drawn over the label's
    // range by the snippet's stylesheet (Liquid prints the first value)
    function paintRange() {
      var cur = currentVariant();
      var list = cur ? spans(cur) : [];
      if (!list.length || !data.rangeText) return;
      var lo = list[0];
      var hi = list[0];
      list.forEach(function (v) {
        if (v.min < lo.min) lo = v;
        if (v.max > hi.max) hi = v;
      });
      var text = data.rangeText.replace('__MIN__', lo.minText).replace('__MAX__', hi.maxText);
      root.style.setProperty('--rd-height-span', '"' + text.replace(/["\\]/g, '') + '"');
    }

    // a switch is on its way: the snippet's stylesheet keeps the height
    // from showing the old span's red for a height another span holds
    function busy(on) {
      root.classList.toggle('rd-span-switching', on);
    }

    // the text and number fields, not the radios (see the header)
    function lockInputs(on) {
      var b = box();
      if (!b) return;
      Array.prototype.forEach.call(b.querySelectorAll('input'), function (el) {
        if (el.type !== 'text' && el.type !== 'number') return;
        if (on) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
    }

    function lock() {
      var b = box();
      if (!b) return;
      var active = document.activeElement;
      // leaving the field fires its change now, while the old span is in
      if (active && b.contains(active) && typeof active.blur === 'function') active.blur();
      lockInputs(true);
    }

    /* What the shopper sees while a span comes in stays as it was. The swap
       shows the span variant's own 0 SEK, then the customizer's "Från ...",
       and empties the fields until the entries go back - on screen that
       reads as the page reloading. So the price is a still copy over the
       live one until the entries are back and priced, and the emptied
       fields show the entries through the stylesheet's field hints, in the
       look of a filled field (rd-span-hold-*, --rd-hold-*). Presentation
       only: the live price changes underneath, unseen, where the customizer
       reads its base as always, and the copy has none of its classes. The
       price and the customizer wrapper are held at their height, so nothing
       below them moves. The radios stay free to click. */
    var still = null;

    function textBox(el) {
      var range = document.createRange();
      range.selectNodeContents(el);
      return range.getBoundingClientRect();
    }

    function hold() {
      if (still) return;
      var price = node('price-');
      var shown = price && price.querySelector('.price__regular .price-item--regular');
      var b = box();
      still = { price: price, box: b, priceMin: price ? price.style.minHeight : '', boxMin: b ? b.style.minHeight : '' };
      if (shown && price.parentNode) {
        var host = price.parentNode;
        var hr = host.getBoundingClientRect();
        var sr = shown.getBoundingClientRect();
        var face = getComputedStyle(shown);
        var copy = document.createElement('span');
        copy.className = 'rd-span-still';
        copy.setAttribute('aria-hidden', 'true');
        copy.textContent = shown.textContent.trim();
        ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'color'].forEach(function (k) { copy.style[k] = face[k]; });
        copy.style.whiteSpace = 'nowrap';
        copy.style.left = (sr.left - hr.left - host.clientLeft) + 'px';
        copy.style.top = (sr.top - hr.top - host.clientTop) + 'px';
        host.classList.add('rd-span-host');
        host.appendChild(copy);
        // the copy is a box as tall as its line height, the price an inline
        // run as tall as its font: line up the text, not the boxes (4 px
        // low otherwise, at 20px type on a 32px line)
        var want = textBox(shown);
        var got = textBox(copy);
        if (want.height && got.height) {
          copy.style.top = (parseFloat(copy.style.top) + want.top - got.top) + 'px';
          copy.style.left = (parseFloat(copy.style.left) + want.left - got.left) + 'px';
        }
        price.style.minHeight = price.getBoundingClientRect().height + 'px';
        price.classList.add('rd-span-held');
        still.copy = copy;
        still.host = host;
      }
      if (b) b.style.minHeight = b.getBoundingClientRect().height + 'px';
      // the entries as the fields showed them: a number field writes 80,5
      [['max-width-', 'w', true], ['max-height-', 'h', true], ['mounting-height-', 'mh', false]].forEach(function (f) {
        var value = saved[f[0] + id];
        if (!value) return;
        var text = f[2] ? String(value).replace('.', ',') : String(value);
        root.style.setProperty('--rd-hold-' + f[1], '"' + text.replace(/["\\]/g, '') + '"');
        root.classList.add('rd-span-hold-' + f[1]);
      });
    }

    function release() {
      if (!still) return;
      if (still.copy && still.copy.parentNode) still.copy.parentNode.removeChild(still.copy);
      if (still.host) still.host.classList.remove('rd-span-host');
      if (still.price) {
        still.price.classList.remove('rd-span-held');
        still.price.style.minHeight = still.priceMin;
      }
      if (still.box) still.box.style.minHeight = still.boxMin;
      ['w', 'h', 'mh'].forEach(function (k) {
        root.classList.remove('rd-span-hold-' + k);
        root.style.removeProperty('--rd-hold-' + k);
      });
      still = null;
    }

    // as a shopper clearing them would; see the header for why
    function clearPriceInputs() {
      clearing = true;
      try {
        ['max-width-', 'max-height-'].forEach(function (prefix) {
          var field = node(prefix);
          if (!field || field.value === '') return;
          field.value = '';
          field.dispatchEvent(new Event('input', { bubbles: true }));
          field.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } finally {
        clearing = false;
      }
    }

    function remember(field) {
      if (field.type === 'hidden') return;
      if (field.type === 'radio' || field.type === 'checkbox') {
        if (field.checked && field.name) saved['radio:' + field.name] = field.value;
      } else if (field.id) {
        saved[field.id] = field.value;
      }
    }

    function spanRadio(value) {
      var picker = node('variant-selects-');
      if (!picker) return null;
      var fieldsets = picker.querySelectorAll('fieldset');
      for (var i = 0; i < fieldsets.length; i++) {
        var legend = fieldsets[i].querySelector(':scope > legend');
        if (!legend || legend.getAttribute('data-legend') !== data.rangeName) continue;
        var radios = fieldsets[i].querySelectorAll('input[type="radio"]');
        for (var j = 0; j < radios.length; j++) {
          if (radios[j].value === value) return radios[j];
        }
      }
      return null;
    }

    // the theme's own sign of a failed refetch: setUnavailable() writes
    // this text on the buy button (global.js, read)
    function watchFailure() {
      unwatchFailure();
      var form = node('product-form-');
      var label = form && form.querySelector('[name="add"] > span');
      var word = window.variantStrings && window.variantStrings.unavailable;
      if (!label || !word || !('MutationObserver' in window)) return;
      failWatch = new MutationObserver(function () {
        if (switching && label.textContent.trim() === String(word).trim()) failed();
      });
      failWatch.observe(label, { childList: true, characterData: true, subtree: true });
    }
    function unwatchFailure() {
      if (failWatch) failWatch.disconnect();
      failWatch = null;
    }

    function failed() {
      if (!switching) return;
      unwatchFailure();
      clearTimeout(watchdog);
      clearTimeout(settle);
      switching = null;
      busy(false);
      lockInputs(false);
      release();
      say({ kind: 'failed' });
      // the width and height were emptied for the switch
      notes.refresh();
    }

    function go() {
      pending = null;
      if (switching) return; // the rebuild on its way checks again
      var target = dueSpan();
      // two tries: a span the theme does not select is left to the customizer.
      // No switch after all: measureNotes judges what the event left
      if (!target || retries >= 2) { busy(false); notes.refresh(); return; }
      var radio = spanRadio(target.options[index]);
      if (!radio) { busy(false); notes.refresh(); return; }
      retries += 1;
      switching = target; // before lock(): its change event must not start another
      clearTimeout(watchdog);
      watchdog = setTimeout(failed, FAIL_MS);
      // in this order: what is on screen is held, the field is left while
      // the old span is in, then the plain base goes on display, then the
      // span is picked
      hold();
      lock();
      clearPriceInputs();
      watchFailure();
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function onEntry(e) {
      if (clearing) return;
      var field = e.target;
      var b = box();
      if (!field || field.tagName !== 'INPUT' || !b || !b.contains(field)) return;
      if (e.isTrusted) retries = 0;
      remember(field);
      if (switching || field !== heightInput()) return;
      // a number that may still grow waits while it is being typed, and says
      // nothing yet (on the way to 250, "25" is below every span); left, or
      // with Enter, it goes as it is
      var growing = e.type === 'input' && canGrow();
      var p = growing ? null : problem();
      say(p);
      if (growing || p || !dueSpan()) {
        clearTimeout(pending);
        pending = null;
        busy(false);
        return;
      }
      // queued already (restore() fires input, then change): keep it
      if (pending) return;
      busy(true);
      // after this event, so the customizer has taken it first
      pending = setTimeout(go, 0);
    }

    // The shopper's Vägg/Tak, Vänster/Höger and roll direction, back in the
    // rebuilt fields as clicking them would. The swap brings the defaults;
    // these are form fields only - the customizer prices width and height
    // alone (isCustomizerInput, read) - so they go back as soon as the new
    // fields are in, before the next paint, rather than after SETTLE.
    // Radios only: a checkbox may carry a price (the gardin page's sy upp)
    // and goes back with the entries in restore()
    function radiosBack() {
      var b = box();
      if (!b) return;
      b.querySelectorAll('input[type="radio"]').forEach(function (field) {
        var value = saved['radio:' + field.name];
        if (value === undefined || field.value !== value || field.checked) return;
        field.checked = true;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // the shopper's entries into the rebuilt fields, as typing them would
    function restore() {
      settle = null;
      switching = null;
      clearTimeout(watchdog);
      unwatchFailure();
      graceUntil = Date.now() + GRACE;
      busy(false);
      lockInputs(false);
      var b = box();
      if (!b) { release(); return; }
      var active = document.activeElement;
      var refocus = focusId && (!active || active === document.body) ? focusId : null;
      radiosBack();
      var touched = [];
      b.querySelectorAll('input').forEach(function (field) {
        if (field.type === 'hidden' || field.type === 'radio') return;
        if (field.type === 'checkbox') {
          var value = saved['radio:' + field.name];
          if (value !== undefined && field.value === value && !field.checked) {
            field.checked = true;
            touched.push(field);
          }
        } else if (field.id && saved[field.id] && field.value !== saved[field.id]) {
          field.value = saved[field.id];
          touched.push(field);
        }
      });
      touched.forEach(function (field) {
        if (field.type !== 'checkbox') field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      // the customizer has repriced them synchronously: what the page shows
      // now is final, and the still copy and the held look can go
      release();
      // the rebuild took the field from under the shopper's cursor
      if (refocus) {
        var again = document.getElementById(refocus);
        if (again && b.contains(again)) {
          try { again.focus({ preventScroll: true }); } catch (err) { /* a courtesy only */ }
        }
      }
    }

    function rebuilt() {
      paintRange();
      // the rebuild took the message line with the old fields
      say(null);
      if (switching || Date.now() < graceUntil) {
        // the new fields are inert as the old ones were, until the entries are back
        if (switching) lockInputs(true);
        radiosBack();
        clearTimeout(settle);
        settle = setTimeout(restore, SETTLE);
        return;
      }
      // the shopper's own variant change: the theme's usual fresh fields
      saved = {};
      busy(false);
      release();
    }

    // measureNotes leaves the height to this code, and holds its own judgement
    // while a switch is queued or out (the fields are emptied and typed back)
    notes.attach({
      busy: function () { return !!switching || !!pending; },
      widthVaries: widthVaries
    });
    root.addEventListener('input', onEntry);
    root.addEventListener('change', onEntry);
    // the same rule for every variant change, not only this code's: a pill
    // or chip click has already taken the focus, but the theme's own
    // follow-up change (autoSelectFirstAvailableOptions) can come while the
    // shopper is back in a field. Capture, so it runs before the theme's
    // handler on the same element starts the refetch
    var picker = node('variant-selects-');
    if (picker) {
      picker.addEventListener('change', function () {
        var b = box();
        var active = document.activeElement;
        if (b && active && b.contains(active) && typeof active.blur === 'function') active.blur();
      }, true);
    }
    document.addEventListener('focusin', function (e) {
      var b = box();
      focusId = b && b.contains(e.target) && e.target.id ? e.target.id : null;
    });
    // a tap outside the fields: the shopper has moved on, no focus comes back
    document.addEventListener('pointerdown', function (e) {
      var b = box();
      if (!b || !b.contains(e.target)) focusId = null;
    }, true);

    // A span variant without limits (a data error: Cord Control creme's
    // Cream/Grå 161-180 had no metaobject and cost 0 kr) gets no height
    // field, so nothing here could lead away from it - the row comes back
    // for as long as it is selected, and the shopper picks as on the old page
    function fallback() {
      var on = !currentVariant();
      if (on === root.classList.contains('rd-span-pills')) return;
      root.classList.toggle('rd-span-pills', on);
      // hidden, the row's pills were left out of segmentWidth's measure
      root.dispatchEvent(new CustomEvent('solskydd:measure'));
    }

    // the theme rewrites #customizer- on every variant change; a new height
    // field is the sign it was rebuilt rather than merely repriced
    var b = box();
    if (b && 'MutationObserver' in window) {
      new MutationObserver(function () {
        fallback();
        var input = heightInput();
        if (!input || input === knownInput) return;
        knownInput = input;
        rebuilt();
      }).observe(b, { childList: true, subtree: true });
    }

    fallback();
    paintRange();
    say(null);
  }

  /* ---------- whole centimetres in the measurement fields --------
     Width, height and mounting height take whole centimetres only: the
     comma - and the point, which the number fields read as the same
     decimal - is never typed in. Its key is refused, and so is a paste or
     a drop that holds one. A decimal between two of the customizer's size
     groups is priced at nothing (theme-gotchas 36), and "130,5" typed
     into the height switches the span at 130 and loses the ",5". The
     customizer has no key or beforeinput handling of its own, so it is
     unaffected: its fields are only read here. */
  function wholeNumbers(root) {
    var id = root.id;

    function isMeasure(t) {
      var box = document.getElementById('customizer-' + id);
      return !!t && t.tagName === 'INPUT' && (t.type === 'number' || t.type === 'text') && !!box && box.contains(t);
    }

    root.addEventListener('keydown', function (e) {
      if ((e.key === ',' || e.key === '.' || e.key === 'Decimal') && isMeasure(e.target)) e.preventDefault();
    }, true);
    // a phone keyboard sends no key, only the text it inserts
    root.addEventListener('beforeinput', function (e) {
      if (!isMeasure(e.target)) return;
      var text = e.data;
      if ((text === null || text === undefined) && e.dataTransfer) text = e.dataTransfer.getData('text/plain');
      if (text && /[.,]/.test(text)) e.preventDefault();
    }, true);
  }

  /* ---------- free shipping meter ------------------------------
     Mirrors cart.js:104. The threshold is in SHOP currency while the cart
     total is in presentment currency, so the cart total is normalised back to
     shop currency before comparing, and the remainder is formatted forward
     again by formatMoney. On a cart change the whole block is re-rendered from
     the server, because two of the three exclusions (a product tag and a
     metafield) are not visible in /cart.js.                      */
  function shippingMeter(root) {
    var meter = root.querySelector('[data-solskydd-shipping]');
    if (!meter) return;

    var threshold = parseInt(meter.dataset.threshold, 10) || 0;
    var fill = meter.querySelector('[data-shipping-fill]');
    var text = meter.querySelector('[data-shipping-text]');
    if (!threshold) return;

    function paint(cartTotalPresentment) {
      var cartTotal = cartTotalPresentment;
      if (meter.dataset.shopCurrency !== meter.dataset.targetCurrency) {
        var rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
        cartTotal = Math.round(cartTotal / rate);
      }
      var left = Math.max(0, threshold - cartTotal);
      var pct = Math.max(0, Math.min(100, (cartTotal / threshold) * 100));
      if (fill) fill.style.width = pct + '%';
      if (!text) return;
      if (left === 0) {
        text.innerHTML = meter.dataset.reachedText || '';
      } else {
        var tpl = meter.dataset.remainingText || '';
        text.innerHTML = tpl.replace('[amount]', '<strong>' + money(left) + '</strong>');
      }
    }

    paint(parseInt(meter.dataset.cartTotal, 10) || 0);
  }

  /* Re-render the meter from the server on a cart change, so the exclusion
     rules are re-evaluated: a "no free shipping" tag or the
     custom.no_free_shipping metafield cannot be seen from /cart.js, only the
     _Long_Goods line item property can.

     Bound once per section. It used to be bound inside shippingMeter(), which
     every re-render runs again for the new markup - two more listeners each
     time, so each add doubled the section refetches: 1, 2, 4 ... 422 after ten
     adds in one page view, 386 in flight at once. That flood can crowd out the
     variant refetch a span switch needs (theme-gotchas 41, 42).
     One request at a time: a change while one is out asks for a single pass
     after it. The section id comes from the root, which is there in both
     states - the meter, or the "not eligible" line that carries no data. */
  function shippingRefresh(root) {
    var busy = false;
    var again = false;

    function refresh() {
      // a section the theme editor has replaced keeps no listeners of its own
      if (!root.isConnected) return;
      if (!root.querySelector('[data-solskydd-shipping], [data-shipping-not-eligible]')) return;
      if (busy) { again = true; return; }
      busy = true;
      fetch(window.location.pathname + '?section_id=' + encodeURIComponent(root.id))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-solskydd-shipping], [data-shipping-not-eligible]');
          var current = root.querySelector('[data-solskydd-shipping], [data-shipping-not-eligible]');
          if (!fresh || !current || !current.parentNode) return;
          current.parentNode.replaceChild(fresh, current);
          shippingMeter(root);             // paint the new markup; binds nothing
        })
        .catch(function () { /* leave the server-rendered state alone */ })
        .then(function () {
          busy = false;
          if (again) { again = false; refresh(); }
        });
    }

    document.addEventListener('ajaxProduct:added', refresh);
    document.addEventListener('cart:updated', refresh);
  }

  /* ---------- sticky buy bar -----------------------------------
     Appears once the real buy button has scrolled UP out of view.

     It must not appear while the buy button is still below the fold,
     which is the state you land in below desktop: there the buy panel
     sits under a full-height gallery, so on a 375x812 screen the button
     starts around 1290px down. "Not intersecting" is true in both
     directions, so testing it alone showed the bar on arrival at the
     top. boundingClientRect.top < 0 distinguishes the two: combined
     with !isIntersecting it means the anchor is fully above the
     viewport, i.e. genuinely scrolled past.                        */
  function stickyBar(root) {
    var bar = document.querySelector('[data-solskydd-sticky]');
    var anchor = root.querySelector('.product-block__buy_buttons');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', scrolledPast);
    }, { rootMargin: '0px 0px -80px 0px' }).observe(anchor);

    var cta = bar.querySelector('[data-sticky-cta]');
    if (cta) {
      cta.addEventListener('click', function () {
        var submit = root.querySelector('product-form [type="submit"]');
        if (submit && !submit.disabled) return submit.click();
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // mirror the live price and the selected variant summary
    var priceOut = bar.querySelector('[data-sticky-price]');
    var variantOut = bar.querySelector('[data-sticky-variant]');

    function mirrorPrice() {
      if (!priceOut) return;
      var price = root.querySelector('.price .price-item--regular, .price__regular .price-item');
      var txt = price ? price.textContent.trim() : '';
      // renderProductInfo fires TWICE per switch and the first pass renders an
      // empty price wrapper; writing that would blank the bar for ~400 ms.
      if (txt) priceOut.textContent = txt;
    }

    function mirrorVariant() {
      if (!variantOut) return;
      // not a row the page hides (the height span, solskydd-spans.liquid)
      var shown = Array.prototype.filter.call(
        root.querySelectorAll('.product-form__input input:checked'),
        function (el) {
          var row = el.closest('fieldset');
          return !row || getComputedStyle(row).display !== 'none';
        }
      );
      var parts = shown.map(function (el) { return (el.value || '').trim(); }).filter(Boolean);
      variantOut.textContent = parts.join(' · ');
    }

    // The radio is checked synchronously, so the variant summary is already
    // correct on change. The PRICE is not - it arrives with the refetch.
    root.addEventListener('change', mirrorVariant);

    /* Same signal as the delivery estimate below: variant:changed is never
       dispatched, and the raw change event fires BEFORE the refetch lands, so
       mirroring on it pinned the bar to the previous variant's price (268 cm
       selected, bar still reading the 132 cm price). Watch the node
       renderProductInfo actually rewrites instead. Coalesced with setTimeout,
       not rAF - rAF is paused in a background tab and would latch `queued`. */
    var queued = false;
    var priceNode = document.getElementById('price-' + root.id);
    if (priceNode && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () { queued = false; mirrorPrice(); }, 0);
      }).observe(priceNode, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    }

    mirrorPrice();
    mirrorVariant();
  }

  /* ---------- FAQ accordion ------------------------------------
     <details> keeps working without JS; this only adds the height
     transition, so a closed panel is still readable to crawlers. */
  function faq(root) {
    var DUR = 340;
    root.querySelectorAll('[data-solskydd-faq-item]').forEach(function (item) {
      var body = item.querySelector('[data-solskydd-faq-body]');
      var summary = item.querySelector('summary');
      if (!body || !summary) return;
      var timer;

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        clearTimeout(timer);

        if (item.open) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () { body.style.height = '0px'; });
          timer = setTimeout(function () {
            item.open = false;
            body.style.height = '';
          }, DUR);
        } else {
          item.open = true;
          body.style.height = '0px';
          requestAnimationFrame(function () {
            body.style.height = body.scrollHeight + 'px';
          });
          timer = setTimeout(function () { body.style.height = ''; }, DUR);
        }
      });
    });
  }

  /* ---------- help-row link that opens an accordion item --------
     A help-row link keeps its URL setting as its href, which is what
     no-JS visitors get and what a product without the item falls back
     to (the item is skipped when its content is empty). When the item
     is on the page, the click opens it through its own summary, so it
     animates like a normal click, and scrolls it to the top. html's
     scroll-padding-top already clears the fixed header. Modified clicks
     still go to the page, so "open in new tab" works.
     data-solskydd-faq-contains (Link 1, the sample) also requires the item
     to link to that URL - otherwise the click follows the href.       */
  function faqLinks(root) {
    root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-solskydd-faq-link]');
      if (!link || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // both sides are handleized in Liquid, so the key is a safe token
      var key = link.getAttribute('data-solskydd-faq-link');
      var item = root.querySelector('[data-solskydd-faq-key="' + key + '"]');
      var summary = item && item.querySelector('summary');
      if (!summary) return;

      var contains = link.getAttribute('data-solskydd-faq-contains');
      if (contains && !item.querySelector('a[href*="' + contains.replace(/["\\]/g, '\\$&') + '"]')) return;

      e.preventDefault();
      if (!item.open) summary.click();
      summary.focus({ preventScroll: true });
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ---------- colour swatch hover label ------------------------
     Hovering a colour swatch previews that colour's name in the
     group label, then restores the selected one. Sibling swatches
     are bound once. Colour chips from a variant option are
     delegated from the panel, because the theme rewrites the
     picker on every variant change; their label is the legend's
     <strong data-variant-legend>, and "selected" is re-read from
     the checked radio each time rather than cached.              */
  function swatchLabel(root) {
    var labelEl = root.querySelector('.product-block__sibling_products strong');
    if (labelEl) {
      var selected = labelEl.textContent.trim();

      root.querySelectorAll('.rd-options .sibling-product').forEach(function (link) {
        var nameEl = link.querySelector('.visually-hidden');
        if (!nameEl) return;
        var name = nameEl.textContent.trim();
        var show = function () { labelEl.textContent = name; };
        var reset = function () { labelEl.textContent = selected; };
        link.addEventListener('mouseenter', show);
        link.addEventListener('mouseleave', reset);
        link.addEventListener('focus', show);
        link.addEventListener('blur', reset);
      });
    }

    var panel = root.querySelector('.rd-options');
    if (!panel) return;

    // the radio behind a chip: from the label (pointer) or the radio itself
    // (keyboard - the theme keeps it focusable but visually hidden)
    function chipRadio(el) {
      if (!el || !el.closest) return null;
      var label = el.closest('.product-form__input label.swatch');
      if (label) return document.getElementById(label.htmlFor);
      var next = el.nextElementSibling;
      if (el.type === 'radio' && next && next.matches('label.swatch')) return el;
      return null;
    }
    function legendOf(radio) {
      var fieldset = radio.closest('fieldset');
      return fieldset && fieldset.querySelector('[data-variant-legend]');
    }
    function show(e) {
      var radio = chipRadio(e.target);
      var legend = radio && legendOf(radio);
      if (legend) legend.textContent = radio.value;
    }
    function reset(e) {
      var radio = chipRadio(e.target);
      if (!radio) return;
      // still on this chip, or moving onto a neighbour that names itself
      var next = chipRadio(e.relatedTarget);
      if (next && next.name === radio.name) return;
      var legend = legendOf(radio);
      var checked = radio.closest('fieldset').querySelector('input[type="radio"]:checked');
      if (legend && checked) legend.textContent = checked.value;
    }
    panel.addEventListener('mouseover', show);
    panel.addEventListener('mouseout', reset);
    panel.addEventListener('focusin', show);
    panel.addEventListener('focusout', reset);
  }

  /* ---------- colour swatch hover -> hero preview ---------------
     Hovering a swatch lays that colour's own main image over the hero,
     so the range can be compared without a page hop per colour. The
     image comes from data-preview, which solskydd-colour-swatches.liquid
     only emits on the OTHER colours - the current one is already on
     screen, so its swatch stays inert.

     A layer on top of the slider rather than a src swap on the slide:
     the theme owns that DOM (scroll position, srcset, lazy loading, the
     zoom modal's item ids), so nothing has to be restored on the way out
     and a mid-hover variant change cannot strand a foreign image in the
     gallery. The stylesheet aligns the layer with the hero box.

     Pointer-driven and hover-capable only. On touch there is no hover,
     and the synthetic pointerenter a tap fires would flash the preview
     for the few frames before the navigation it belongs to.         */
  function swatchPreview(root) {
    var slider = root.querySelector('.product-media-slider');
    var row = root.querySelector('.product-block__sibling_products');
    if (!slider || !row) return;
    if (!window.matchMedia || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var layer = null;
    // bumped on every show/hide, so a slow image that lands after the pointer
    // has moved on is dropped instead of overwriting the newer one
    var turn = 0;

    function overlay() {
      if (!layer || !layer.isConnected) {
        layer = document.createElement('img');
        layer.className = 'rd-swatch-preview';
        layer.alt = '';
        layer.setAttribute('aria-hidden', 'true');
        layer.setAttribute('draggable', 'false');
        slider.appendChild(layer);
      }
      return layer;
    }

    function show(src) {
      if (!src) return;
      var el = overlay();
      var mine = ++turn;
      var pre = new Image();
      var apply = function () {
        pre.onload = null;
        if (mine !== turn) return;
        el.src = src;
        el.classList.add('is-visible');
      };
      // only reveal once the bytes are in, otherwise the first hover of each
      // colour fades in an empty box while the image is still downloading
      pre.onload = apply;
      pre.src = src;
      if (pre.complete) apply();
    }

    function hide() {
      turn++;
      if (layer) layer.classList.remove('is-visible');
    }

    // Warm the previews once the pointer reaches the buy panel. Measured cold
    // on the dev store, the first hover of a colour otherwise waits ~1s for its
    // image while the swatch sits there doing nothing; by the time the pointer
    // has crossed the panel to the swatch row the bytes are usually in. Nothing
    // is fetched for a visitor who only ever touches the gallery.
    var panel = row.closest('.rd-options') || row;
    panel.addEventListener('pointerenter', function () {
      row.querySelectorAll('a.sibling-product[data-preview]').forEach(function (link) {
        new Image().src = link.dataset.preview;
      });
    }, { once: true });

    row.querySelectorAll('a.sibling-product[data-preview]').forEach(function (link) {
      link.addEventListener('pointerenter', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        show(link.dataset.preview);
      });
      link.addEventListener('pointerleave', hide);
      link.addEventListener('focus', function () { show(link.dataset.preview); });
      link.addEventListener('blur', hide);
    });

    // a fast sweep off the row can outrun a swatch's own pointerleave
    row.addEventListener('pointerleave', hide);
  }

  /* ---------- shop-the-look lightbox --------------------------- */
  function ugc(root) {
    var modal = document.querySelector('[data-solskydd-ugc-modal]');
    if (!modal) return;

    var img = modal.querySelector('[data-ugc-image]');
    var name = modal.querySelector('[data-ugc-name]');
    var list = modal.querySelector('[data-ugc-products]');

    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    function open(card) {
      if (img) {
        img.src = card.dataset.image || '';
        img.alt = card.dataset.imageAlt || '';
      }
      if (name) name.textContent = card.dataset.name || '';
      if (list) {
        var scope = card.parentNode;   // the card's own wrapper holds its <template>
        var source = scope && scope.querySelector('[data-ugc-card-products]');
        list.innerHTML = source ? source.innerHTML : '';
      }
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      var closeBtn = modal.querySelector('[data-ugc-close]');
      if (closeBtn) closeBtn.focus();
    }

    root.querySelectorAll('[data-solskydd-ugc-card]').forEach(function (card) {
      card.addEventListener('click', function () { open(card); });
    });
    modal.querySelectorAll('[data-ugc-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  /* ---------- reserve the shop-the-look slot -------------------
     The Cevoid gallery injects its markup about 400ms after load,
     which grows the document by its own height under anyone already
     reading near it. Measured at 167px on a 375px viewport.

     Reserving the height up front and releasing it once the app has
     rendered keeps the document height constant across the injection,
     without leaving a gap on products whose gallery is empty - the
     block's settings hide it in that case, and the release still runs
     on the timeout.                                                */
  var SHOP_SLOT_RESERVE = 167;

  function reserveShopSlot(root) {
    var shop = root.querySelector('.rd-shop');
    var slot = shop && shop.querySelector('.shopify-block');
    if (!shop || !slot) return;

    // The app ships a server-rendered shell (its own block div plus
    // style/script tags) and fills it later, so child count says
    // nothing - only the rendered height does.
    if (slot.getBoundingClientRect().height >= SHOP_SLOT_RESERVE) return;

    // Reserve on the section, leaving the slot free to be measured.
    var base = Math.round(shop.getBoundingClientRect().height);
    shop.style.minHeight = (base + SHOP_SLOT_RESERVE) + 'px';

    var released = false;
    function release() {
      if (released) return;
      released = true;
      shop.style.minHeight = '';
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        // most of the way there is enough; the last pixels are the
        // app's own images settling and are not worth holding for
        if (slot.getBoundingClientRect().height < SHOP_SLOT_RESERVE * 0.6) return;
        ro.disconnect();
        release();
      });
      ro.observe(slot);
      setTimeout(function () { ro.disconnect(); release(); }, 5000);
    } else {
      setTimeout(release, 5000);
    }
  }

  /* ---------- colour switch continuity -------------------------
     Each colour is its own product, so a swatch is a real <a> and
     switching colour is a full navigation - the browser lands the new
     page at scroll 0 and the shopper loses the row they were reading.

     This keeps the swatch row at the same viewport offset across the
     hop, so the page appears to stay put and only the contents change.
     It is a progressive enhancement: with JS off, or when anything
     below is missing, the link behaves exactly as before.

     Only applied to a fresh 'navigate'. On back/forward the browser has
     its own (better) scroll restoration and must be left alone.     */
  function siblingNav(root) {
    var KEY = 'solskydd:sibling-nav';
    var row = root.querySelector('.product-block__sibling_products');
    if (!row) return;

    root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a.sibling-product');
      if (!link || link.classList.contains('current-product')) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({
          offset: Math.round(row.getBoundingClientRect().top),
          t: Date.now()
        }));
      } catch (err) { /* private mode: fall back to a normal navigation */ }

      // Carry a ?view= override across the hop. Only present while
      // previewing the template before the sibling products have their
      // template assigned in the admin; in production there is no
      // param and the href is left untouched.
      //
      // Built through the URL API rather than string concatenation because
      // the href ends in the #MainContent fragment - appending "?view=..."
      // to that would land inside the fragment and be lost.
      var view = new URLSearchParams(location.search).get('view');
      if (!view) return;
      try {
        var next = new URL(link.href, location.href);
        if (!next.searchParams.has('view')) {
          next.searchParams.set('view', view);
          link.href = next.toString();
        }
      } catch (err) { /* leave the href as authored */ }
    });

    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type !== 'navigate') return;

    var raw = null;
    try {
      raw = sessionStorage.getItem(KEY);
      sessionStorage.removeItem(KEY);
    } catch (err) { return; }
    if (!raw) return;

    var saved;
    try { saved = JSON.parse(raw); } catch (err) { return; }
    // only honour a hop that just happened, never a stale tab
    if (!saved || typeof saved.offset !== 'number' || Date.now() - saved.t > 10000) return;

    // Stop the moment the shopper takes over; never fight a real scroll.
    var userMoved = false;
    ['wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, function () { userMoved = true; }, { once: true, passive: true });
    });

    function place() {
      if (userMoved) return;
      var y = row.getBoundingClientRect().top + window.pageYOffset - saved.offset;
      // The theme sets scroll-behavior:smooth on <html>, so a plain
      // scrollTo here animates - which both looks like the lurch this
      // is meant to remove and loses races against the browser's own
      // scroll-to-top on a fresh navigation. Force it to jump.
      var docEl = document.documentElement;
      var prev = docEl.style.scrollBehavior;
      docEl.style.scrollBehavior = 'auto';
      window.scrollTo(0, Math.max(0, Math.round(y)));
      docEl.style.scrollBehavior = prev;
    }

    // The gallery above the row has no final height until its images
    // decode, so a single pass lands short and then visibly corrects -
    // the same lurch from the other direction. Re-place on every change
    // in document height until it holds still, which converges during
    // load and is therefore never seen.
    place();
    var deadline = Date.now() + 2000;
    var lastHeight = -1;
    (function settle() {
      var h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        place();
      }
      if (!userMoved && Date.now() < deadline) requestAnimationFrame(settle);
    })();
    window.addEventListener('load', place, { once: true });
  }

  /* ---------- boot --------------------------------------------- */
  function init(root) {
    // once per section root; the theme editor's section reload brings a new one
    if (!root || root.dataset.solskyddReady) return;
    root.dataset.solskyddReady = 'true';
    segmentWidth(root);
    optionSelects(root);
    var notes = measureNotes(root);
    heightSpans(root, notes);
    // after heightSpans, on purpose: its listeners must run first, so that a
    // span switch it queues on an event is known (busy) when measureNotes
    // judges that same event. Reordered, the width would be judged against
    // the span about to be left
    notes.listen();
    wholeNumbers(root);
    shippingMeter(root);
    shippingRefresh(root);
    stickyBar(root);
    reserveShopSlot(root);
    siblingNav(root);
    faq(root);
    faqLinks(root);
    swatchLabel(root);
    deliveryEstimate(root);
    swatchPreview(root);
    ugc(root);
  }

  /* ---------- delivery date estimate --------------------------
     Turns the store's stated lead times into real dates:
     "Leverans 18-24 sep" rather than "inom 2-6 arbetsdagar".

     Client-side on purpose. The same line rendered in Liquid would be
     baked into Shopify's full-page cache and could be served a day stale;
     the snippet therefore ships a plain business-day sentence and this
     overwrites it on load. Dates are resolved in Europe/Stockholm, not the
     shopper's zone - the promise is about when the warehouse ships.       */
  function deliveryEstimate(root) {
    var box = root.querySelector('[data-solskydd-delivery]');
    if (!box) return;
    var out = box.querySelector('[data-solskydd-delivery-text]');
    if (!out) return;

    var d = box.dataset;
    var num = function (v, fallback) {
      var n = parseInt(v, 10);
      return isNaN(n) ? fallback : n;
    };

    /* Easter Sunday, anonymous Gregorian algorithm. Every moving Swedish
       red day is an offset from it, so this is the only hard part. */
    function easter(year) {
      var a = year % 19, b = Math.floor(year / 100), c = year % 100;
      var dd = Math.floor(b / 4), e = b % 4;
      var f = Math.floor((b + 8) / 25);
      var g = Math.floor((b - f + 1) / 3);
      var h = (19 * a + b - dd - g + 15) % 30;
      var i = Math.floor(c / 4), k = c % 4;
      var l = (32 + 2 * e + 2 * i - h - k) % 7;
      var m = Math.floor((a + 11 * h + 22 * l) / 451);
      var month = Math.floor((h + l - 7 * m + 114) / 31);
      var day = ((h + l - 7 * m + 114) % 31) + 1;
      return new Date(year, month - 1, day);
    }

    var holidayCache = {};
    /* Weekday red days only - Saturdays and Sundays are already excluded, so
       Midsommardagen and Alla helgons dag never need listing. Julafton,
       midsommarafton and nyårsafton are not official holidays but the
       warehouse is shut, and erring long is the safe direction here. */
    function holidays(year) {
      if (holidayCache[year]) return holidayCache[year];
      var key = function (dt) { return dt.getFullYear() + '-' + dt.getMonth() + '-' + dt.getDate(); };
      var e = easter(year);
      var off = function (n) { return new Date(year, e.getMonth(), e.getDate() + n); };
      var list = [
        new Date(year, 0, 1),   // nyårsdagen
        new Date(year, 0, 6),   // trettondedag jul
        off(-2),                // långfredag
        off(1),                 // annandag påsk
        new Date(year, 4, 1),   // första maj
        off(39),                // Kristi himmelsfärdsdag
        new Date(year, 5, 6),   // nationaldagen
        new Date(year, 11, 24), // julafton
        new Date(year, 11, 25), // juldagen
        new Date(year, 11, 26), // annandag jul
        new Date(year, 11, 31)  // nyårsafton
      ];
      // midsommarafton: the Friday that falls on 19-25 June
      for (var day = 19; day <= 25; day++) {
        var cand = new Date(year, 5, day);
        if (cand.getDay() === 5) { list.push(cand); break; }
      }
      var set = {};
      list.forEach(function (dt) { set[key(dt)] = true; });
      holidayCache[year] = { set: set, key: key };
      return holidayCache[year];
    }

    function isBusinessDay(dt) {
      var wd = dt.getDay();
      if (wd === 0 || wd === 6) return false;
      var h = holidays(dt.getFullYear());
      return !h.set[h.key(dt)];
    }

    function addBusinessDays(from, n) {
      var r = new Date(from.getTime()), added = 0;
      while (added < n) {
        r.setDate(r.getDate() + 1);
        if (isBusinessDay(r)) added++;
      }
      return r;
    }

    function snapForward(from) {
      var r = new Date(from.getTime());
      while (!isBusinessDay(r)) r.setDate(r.getDate() + 1);
      return r;
    }

    /* "now" as the warehouse sees it. sv-SE formats as YYYY-MM-DD HH:MM:SS,
       so the parts can be read straight off without a second Date round-trip. */
    function stockholmNow() {
      var parts;
      try {
        parts = new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' });
      } catch (err) {
        parts = null;
      }
      var m = parts && parts.match(/^(\d{4})-(\d{2})-(\d{2})\D+(\d{2})/);
      if (!m) {
        var local = new Date();
        return { date: new Date(local.getFullYear(), local.getMonth(), local.getDate()), hour: local.getHours() };
      }
      return {
        date: new Date(+m[1], +m[2] - 1, +m[3]),
        hour: +m[4]
      };
    }

    /* The page's own language (<html lang>, layout/theme.liquid). Swedish
       keeps the hand-built range below; other languages get Intl's range,
       which knows their day/month order ("Oct 3 – 6", "3.–6.10."). */
    var pageLang = (document.documentElement.lang || 'sv').toLowerCase();
    var swedish = pageLang.indexOf('sv') === 0;

    function label(dt) {
      // sv-SE short months carry a trailing period ("18 sep."); drop it
      return dt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
    }

    function range(a, b) {
      if (!swedish) {
        try {
          var fmt = new Intl.DateTimeFormat(pageLang, { day: 'numeric', month: 'short' });
          if (a.getTime() === b.getTime()) return fmt.format(b);
          return fmt.formatRange ? fmt.formatRange(a, b) : fmt.format(a) + '–' + fmt.format(b);
        } catch (err) { /* unknown language tag: Swedish below */ }
      }
      if (a.getTime() === b.getTime()) return label(b);
      if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
        return a.getDate() + '–' + label(b);
      }
      return label(a) + '–' + label(b);
    }

    /* Made to measure is read off the customizer rather than kept in a
       variable: that element IS in the theme's variant swap list, so it is
       always current after a change, while this block never gets swapped.
       data-custom is the first-paint value for products with no customizer. */
    function isCustom() {
      var cz = document.getElementById('customizer-' + root.id);
      if (!cz) return d.custom === 'true';
      if (cz.classList.contains('hidden')) return false;
      return cz.innerHTML.trim().length > 0;
    }

    function render() {
      var now = stockholmNow();
      var start = new Date(now.date.getTime());
      var cutoff = num(d.cutoff, 0);
      if (cutoff > 0 && now.hour >= cutoff) start.setDate(start.getDate() + 1);

      var custom = isCustom();
      var first, last;
      if (custom) {
        first = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(d.customMin, 21)));
        last = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(d.customMax, 21)));
      } else {
        first = addBusinessDays(start, num(d.warehouseMin, 1) + num(d.transitMin, 1));
        last = addBusinessDays(start, num(d.warehouseMax, 3) + num(d.transitMax, 3));
      }
      if (last < first) last = first;

      // both labels are the block setting or the theme's translation
      // (products.pdp), same as the shipping meter's. The empty check only
      // guards a missing attribute.
      var tpl = custom ? (d.labelCustom || '') : (d.label || '');
      if (!tpl) return;
      out.innerHTML = tpl.split('[range]').join(range(first, last));
    }

    render();

    /* Nothing dispatches variant:changed in this theme, and the raw change
       event fires before the refetch lands, so neither is usable. The price
       and customizer nodes are rewritten by renderProductInfo on every
       variant change - watching them is the one reliable signal. Coalesced
       into a frame because that swap writes several nodes in a row. */
    var queued = false;
    var recalc = function () {
      if (queued) return;
      queued = true;
      // setTimeout rather than requestAnimationFrame: rAF is paused in a
      // background tab, which would leave `queued` latched true and kill
      // every later recalculation until the tab is looked at again.
      setTimeout(function () { queued = false; render(); }, 0);
    };
    ['price-', 'customizer-'].forEach(function (prefix) {
      var node = document.getElementById(prefix + root.id);
      if (!node) return;
      new MutationObserver(recalc).observe(node, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-solskydd-pdp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // re-init after a theme editor section reload
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-solskydd-pdp]');
    if (root) init(root);
  });
})();
